import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Outlet, useNavigate } from "react-router-dom";
import { useEffect, type FunctionComponent } from "react";
import CreateCampaignPreview from "../../../components/create-campaign-preview.components";
import {
	type IzCampaignFormAdmin,
	zCampaignFormAdmin,
} from "../../../z/admin/campaign-form.admin.z";
import {
	Platforms,
	SocialCreatorType,
	SocialGender,
} from "../../../types/models/social.models.types";
import { useCreateCampaignAdminHttp } from "../../../hooks/http/admin/campaigns/create-campaign.admin.http";
import CampaignFormAdminPartial from "../campaign-form.admin.partials";
import { useToast } from "../../../hooks/use-toast";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";
import Spinner from "../../../components/spinner.components";
import type { IAssetMinimal } from "../../../types/models/asset.models.types";
import {
	findErrorMessage,
	handleHttpError,
	sanitizeNumberField,
} from "../../../utils/utils";
import { MilestoneRewardType } from "../../../z/brand/campaign-form.brand.z";


interface IProps {
	brandId: string;
}

export const CreateCampaignAdminPartial: FunctionComponent<IProps> = ({
	brandId,
}) => {
	const { execute: createCampaignHttp, fetching: creatingCampaign } =
		useCreateCampaignAdminHttp();
	const navigate = useNavigate();
	const { execute: uploadAssetHttp, fetching: uploadingAsset } =
		useCreateAssetCommonHttp();

	const { toast } = useToast();

	const form = useForm<IzCampaignFormAdmin>({
		resolver: zodResolver(zCampaignFormAdmin),
		defaultValues: {
			name: "",
			socialCreatorType: SocialCreatorType.meme,
			milestones: [
				{ count: 10000, payout: 500 },
				{ count: 30000, payout: 800 },
				{ count: 100000, payout: 1200 },
				{ count: 150000, payout: 1500 },
				{ count: 300000, payout: 2000 },
				{ count: 800000, payout: 3000 },
				{ count: 1200000, payout: 10000 },
			],
			aboutPayment: `- Payments will be processed within a minimum of 30 working days after the campaign ends.

- The highest milestone will be given only to one creator with the highest number of views.

- The Reel must remain on your feed for at least 45 days from the posting date.

- Insights need to be submitted within 5 days of flagging the post, or the post will be disqualified.

- Hiding likes and comments will result in disqualification.

- Increasing views through story share will be disqualified.

- Any attempt to artificially inflate views through ads, bots, or story seeding will make the creator ineligible for payment.

- Copied content from other pages will lead to disqualification.

- Please share performance insights when requested by the Meme’d team’s POC.

- The campaign may end earlier than the stated end date.`,
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (form.formState.errors) {
			const key = Object.keys(form.formState.errors)[0];
			console.log(key);
			if (key) {
				const error =
					form.formState.errors[key as keyof typeof form.formState.errors];
				const message = findErrorMessage(error) || "An error occurred";

				toast({
					content: message,
					toastType: "error",
				});
			}
		}
	}, [form.formState.errors]);

	async function uploadAsset(file: File) {
		const formData = new FormData();
		formData.append("file", file);
		const response = await uploadAssetHttp({
			body: formData,
		});
		if (!response.success) {
			return false;
		}
		return response.data.asset;
	}

	async function onSubmit(data: IzCampaignFormAdmin) {
		try {
			const genderPreference =
				data.genderPreference === SocialGender.prefernotsay
					? null
					: data.genderPreference;

			if (!data.socialPlatform) {
				return toast({
					content: "Please select a platform",
					toastType: "error",
				});
			}

			if (!data.socialPostType) {
				return toast({
					content: "Please select a post type",
					toastType: "error",
				});
			}

			if (data.coverImageFile.length > 0) {
				const uploadedCoverImage = await uploadAsset(
					data.coverImageFile[0].file,
				);
				if (!uploadedCoverImage) {
					return toast({
						content: "Failed to upload cover image. Please try again",
						toastType: "error",
					});
				}
				form.setValue(
					"coverImage",
					{
						id: uploadedCoverImage.id,
						url: uploadedCoverImage.url,
					},
					{ shouldDirty: true },
				);

				data.coverImage = {
					id: uploadedCoverImage.id,
					url: uploadedCoverImage.url,
				};
			}

			if (data.assetFile && data.assetFile?.length > 0) {
				const uploadedAssets = await Promise.all(
					data.assetFile.map((file) => uploadAsset(file)),
				);
				const failedAssetUploads = uploadedAssets.filter((asset) => !asset);

				if (failedAssetUploads.length > 0) {
					toast({
						content: "Failed to upload some assets. Please try again",
						toastType: "error",
					});
					return;
				}

				const newAssets = uploadedAssets
					.filter((asset): asset is IAssetMinimal => asset !== false)
					.map((asset) => ({
						id: asset.id,
						url: asset.url,
					}));
				if (form.formState.dirtyFields.assetFile) {
					form.setValue("assetFile", []);
				}

				form.setValue(
					"assets",
					[
						...data.assets,
						...newAssets.map((asset) => ({
							id: asset.id,
							url: asset.url,
						})),
					],
					{ shouldDirty: true },
				);

				data.assets = [
					...data.assets,
					...newAssets.map((asset) => ({
						id: asset.id,
						url: asset.url,
					})),
				];
			}
			if (data.sampleVideosFiles && data.sampleVideosFiles?.length > 0) {
				const uploadedSampleVideos = await Promise.all(
					data.sampleVideosFiles.map(async (fileWrapper) => {
						const uploadedSampleVideo = await uploadAsset(fileWrapper);
						if (!uploadedSampleVideo) {
							throw new Error("Failed to upload sample video");
						}
						return uploadedSampleVideo;
					}),
				);

				if (form.formState.dirtyFields.sampleVideos) {
					form.setValue("sampleVideosFiles", []);
				}
				form.setValue(
					"sampleVideos",
					[
						...uploadedSampleVideos.map((video) => ({
							id: video.id,
							url: video.url,
						})),
					],
					{ shouldDirty: true },
				);

				data.sampleVideos = [
					...uploadedSampleVideos.map((video) => ({
						id: video.id,
						url: video.url,
					})),
				];
			}
			if (data.briefVideosFiles && data.briefVideosFiles?.length > 0) {
				const uploadedBriefVideos = await Promise.all(
					data.briefVideosFiles.map(async (fileWrapper) => {
						const uploadedBriefVideo = await uploadAsset(fileWrapper);
						if (!uploadedBriefVideo) {
							throw new Error("Failed to upload brief video");
						}
						return uploadedBriefVideo;
					}),
				);

				if (form.formState.dirtyFields.briefVideos) {
					form.setValue("briefVideosFiles", []);
				}
				form.setValue(
					"briefVideos",
					[
						...uploadedBriefVideos.map((video) => ({
							id: video.id,
							url: video.url,
						})),
					],
					{ shouldDirty: true },
				);

				data.briefVideos = [
					...uploadedBriefVideos.map((video) => ({
						id: video.id,
						url: video.url,
					})),
				];
			}
			if (
				data.productRefund?.productImageFile &&
				data.productRefund?.productImageFile?.length > 0
			) {
				const uploadedProductImage = await Promise.all(
					data.productRefund?.productImageFile.map(async (file) => {
						const uploadedProductImage = await uploadAsset(file);
						if (!uploadedProductImage) {
							throw new Error("Failed to upload product image");
						}
						return uploadedProductImage;
					}),
				);

				if (form.formState.dirtyFields.productRefund) {
					form.setValue("productRefund.productImageFile", []);
				}
				const newImages = uploadedProductImage.map((video) => ({
					id: video.id,
					url: video.url,
					name: video.name,
				}));
				form.setValue("productRefund.image", newImages, { shouldDirty: true });

				if (!data.productRefund) return;

				data.productRefund.image = [
					...uploadedProductImage.map((image) => ({
						id: image.id,
						url: image.url,
						name: image.name,
					})),
				];
			}

			const response = await createCampaignHttp({
				body: {
					name: data.name,
					coverImage: form.formState.dirtyFields.coverImage
						? data.coverImage
							? data.coverImage.id
							: null
						: null,
					socialPlatform: data.socialPlatform,
					favouriteSocial: data.favouriteSocial,
					brand: brandId,
					socialPostType: data.socialPostType,
					socialCreatorType: data.socialCreatorType,
					genderPreference: genderPreference,
					minimumFollowers: sanitizeNumberField(data.minimumFollowers),
					maximumFollowers: sanitizeNumberField(data.maximumFollowers),
					genre: data.genre.map((i) => i.id),
					locationPreference:
						data.locationPreference.length > 0
							? data.locationPreference.map((i) => i.placeId)
							: null,
					startDate: data.startDate,
					hasFixedPayout: data.hasFixedPayout.toString(),
					endDate: data.endDate,
					budget: data.budget,
					budgetInternal: data.budgetInternal,
					milestones: data.milestones,
					milestoneRewardType: MilestoneRewardType.CASH,
					highestMilestoneOnlyForTopPost: "true",
					assets: form.formState.dirtyFields.assets
						? data.assets.map((i) => i.id)
						: [],
					links: data.links || null,

					productRefund: data.productRefund?.enabled
						? {
								image: data.productRefund.image[0].id,
								link: data.productRefund.link,
								couponCode: data.productRefund.couponCode,
								refundAmount: data.productRefund.refundAmount,
							}
						: null,
					sampleVideos: form.formState.dirtyFields.sampleVideos
						? data.sampleVideos.map((i) => i.id)
						: [],
					briefVideos: form.formState.dirtyFields.briefVideos
						? data.briefVideos.map((i) => i.id)
						: [],
					hashtags: data.hashtags,
					mentions: data.mentions,
					aboutCampaign: data.aboutCampaign,
					do: data.do,
					dont: data.dont,
					aboutPayment: data.aboutPayment,
					aboutBrand: data.aboutBrand,
					platform: Platforms.solo,
				},
			});

			if (!response.success) {
				return handleHttpError(response, toast);
			}

			toast({
				content: "Campaign created successfully",
				toastType: "success",
			});

			navigate(`/admin/campaigns/${response.data.campaign.id}/edit`);
		} catch (error) {
			// Catch any unexpected errors
			toast({
				content: "Failed to create campaign. Please try again",
				toastType: "error",
			});
		}
	}

	return (
		<div className="flex flex-col px-6 pl-8 w-full">
			<div className="top-0 z-50 sticky flex justify-between items-center bg-gradient-to-b from-background via-background to-transparent pt-12 w-full">
				<p className="font-semibold text-2xl">Create Campaign</p>
				<div className="flex gap-4">
					<Button
						disabled={uploadingAsset || creatingCampaign}
						onClick={() => {
							document.getElementById("campaign-form-submit-admin")?.click();
						}}
						className="flex justify-center items-center bg-primarybuttonbg w-48 h-12 font-semibold text-base text-primarybuttontext"
					>
						{creatingCampaign || uploadingAsset ? (
							<Spinner width={20} />
						) : (
							"Create Campaign"
						)}
					</Button>
					{/* <Button className="px-8 py-6 font-semibold text-base text-primarybuttontext">
						Save Draft
					</Button> */}
				</div>
			</div>
			<div className="flex">
				<div className="relative w-72 min-h-full">
					<div className="fixed">
						<CreateCampaignPreview campaignForm={form} />
					</div>
				</div>

				<div className="flex flex-col flex-1 my-12 ml-6">
					<CampaignFormAdminPartial form={form} onSubmit={onSubmit} />
				</div>
			</div>
			<Outlet />
		</div>
	);
};
